/**
 * MessageAttachmentItem Component
 * 
 * Displays individual file attachments within user messages.
 * Simplified read-only version focused on clean message display.
 * 
 * Features:
 * - File type icon display based on file extension
 * - File name display with truncation
 * - Tooltip for full filename
 * - Clean, minimal design for message context
 * - Optional download/preview actions
 */

import React from 'react';
import { Box, Text, Tooltip } from '@mantine/core';
import { 
  getFileTypeIconFromFilename, 
  formatFileName 
} from '../AttachmentItem/utils';
import classes from './MessageAttachmentItem.module.css';

// =============================================================================
// TYPES
// =============================================================================

export interface MessageAttachmentItemProps {
  /** The filename of the attached file */
  filename: string;
  /** The message ID this file is associated with */
  messageId: string;
  /** Whether the item is read-only (default: true) */
  readOnly?: boolean;
  /** Optional callback for file actions (download, preview, etc.) */
  onAction?: (action: string, messageId: string, filename: string) => void;
  /** Additional CSS class names */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const MessageAttachmentItem: React.FC<MessageAttachmentItemProps> = ({
  filename,
  messageId,
  readOnly = true,
  onAction,
  className,
  style,
}) => {
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  const handleClick = () => {
    if (!readOnly && onAction) {
      onAction('download', messageId, filename);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  // =============================================================================
  // DERIVED STATE
  // =============================================================================
  
  const FileIconComponent = getFileTypeIconFromFilename(filename);
  const displayFileName = formatFileName(filename, 20); // Shorter for message context
  const isInteractive = !readOnly && onAction;

  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <Tooltip 
      label={filename !== displayFileName ? filename : undefined}
      position="top"
      disabled={filename === displayFileName}
    >
      <Box
        className={`
          ${classes.messageAttachmentItem} 
          ${className || ''} 
          ${isInteractive ? classes.interactive : ''}
        `.trim()}
        style={style}
        onClick={isInteractive ? handleClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        tabIndex={isInteractive ? 0 : -1}
        role={isInteractive ? "button" : undefined}
        aria-label={isInteractive ? `Download ${filename}` : `File: ${filename}`}
      >
        {/* File Icon */}
        <Box className={classes.iconContainer}>
          <FileIconComponent size={14} />
        </Box>

        {/* File Name */}
        <Text className={classes.fileName} size="xs">
          {displayFileName}
        </Text>
      </Box>
    </Tooltip>
  );
};

export default MessageAttachmentItem;